import React from 'react';
import {Box, styled, Tooltip} from '@mui/material';
import {
  designSystemToken,
  designSystemComposition,
  Icon,
  Body,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';

const WordCountContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  height: '24px',
  ...designSystemComposition('typography.body.md'),
}));

const shouldNotForwardProps = ['isOverLimit', 'isUnderLimit'];

const WordCountText = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})<{
  isOverLimit?: boolean;
  isUnderLimit?: boolean;
}>(({isOverLimit, isUnderLimit}) => ({
  color:
    isOverLimit || isUnderLimit
      ? designSystemToken('semantic.fg.danger')
      : designSystemToken('semantic.fg.secondary'),
}));

// Use Icon with appearance directly; no need for a styled wrapper

const InfoRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const StyledTooltipBody = styled(Body)(() => ({
  maxWidth: '185px',
  letterSpacing: '-0.2px',
  textAlign: 'center',
}));

interface WordCountProps {
  testID?: string;
  wordCount: number;
  minWords: number;
  maxWordsWithTolerance: number;
  isEdited?: boolean;
}

function WordCount({
  testID = 'word-count',
  wordCount,
  minWords,
  maxWordsWithTolerance,
  isEdited = false,
}: WordCountProps) {
  const isOverLimit = wordCount > maxWordsWithTolerance;
  const isUnderLimit = wordCount < minWords;
  const isOutOfRange = isOverLimit || isUnderLimit;

  const renderContent = () => {
    if (isOverLimit) {
      return translate('Text too long');
    }
    if (isUnderLimit) {
      return translate('Text too short');
    }

    return (
      <>
        {wordCount} {translate('words')}
        {isEdited && ` (${translate('edited')})`}
      </>
    );
  };

  return (
    <WordCountContainer data-testid={testID}>
      {isOutOfRange ? (
        <Tooltip
          title={
            <StyledTooltipBody size="md">
              {wordCount} {translate('words')} (
              {isOverLimit ? translate('max limit') : translate('min limit')}:{' '}
              {isOverLimit ? maxWordsWithTolerance : minWords})
            </StyledTooltipBody>
          }
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                'bgcolor': designSystemToken('semantic.bg.secondary'),
                'boxShadow': '0px 4px 12px rgba(0, 0, 0, 0.15)',
                '& .MuiTooltip-arrow': {
                  color: designSystemToken('semantic.bg.secondary'),
                },
              },
            },
          }}
        >
          <InfoRow>
            <Icon name="Actions-Info-Fill" size="small" appearance="danger" />
            <WordCountText
              isOverLimit={isOverLimit}
              isUnderLimit={isUnderLimit}
            >
              {renderContent()}
            </WordCountText>
          </InfoRow>
        </Tooltip>
      ) : (
        <WordCountText isOverLimit={isOverLimit} isUnderLimit={isUnderLimit}>
          {renderContent()}
        </WordCountText>
      )}
    </WordCountContainer>
  );
}

export default WordCount;
